/**
 * The console's shared chrome — the design-system primitives every view is
 * built out of (spec §6.1, `components/`).
 *
 * Most of this was ITEM LIST's private code first. It moved here rather than
 * being copied when the Brief, My Work, Item detail and Daily Log needed the
 * same kind chip, the same age column, the same empty panel: a second
 * spelling of "what does a stalled row look like" is a second thing that can
 * disagree with the first, and the whole point of this console is that its
 * views cannot disagree about what is stuck.
 *
 * Everything visual here comes from the source design (`LedAI Webapp v2.html`)
 * — card shell, eyebrow, mono meta line, chip, evidence tile — and every
 * colour comes from `tokens.ts`. No hex literal appears in this file;
 * `tokens.test.ts` scans for them.
 *
 * ## What USED to be here: the ball pill
 *
 * The design draws a "ball-in-court" pill on every work row, and this module
 * exported one. It is gone from the whole console (spec §4): `ballHolder` is
 * a free-text denormalized copy of an assignee, and it reads `Unassigned` on
 * essentially every real row in this account. A pill that prints `Unassigned`
 * everywhere looks exactly like data and is not, which is worse than showing
 * nothing.
 *
 * The FIELD survives, deliberately — `getMyWork` partitions on it and
 * `prepareNudge` addresses a draft with it, so removing it from the
 * projection is a different change with its own blast radius. What is gone is
 * every place the console SHOWED it. `tests/guards/noBallAffordance.test.ts`
 * keeps it gone.
 *
 * `initialsOf` stayed. It is a generic name→initials helper (Invite's roster
 * avatars use it) and was only ever called `ballInitials` because the pill
 * was its first caller.
 *
 * ## An empty state is not a failure, and this module is where that is decided
 *
 * `EmptyPanel` and `FailurePanel` are deliberately two components with two
 * different `data-testid`s, two different eyebrow inks, and two different
 * vocabularies. A view that rendered "nothing here" for a 503 would be lying
 * about the one thing an operator opens this console to learn.
 */

import { PhotoImage } from "./PhotoImage";
import { useEffect, useRef, useState } from "react";
import type { ApiError, ConsoleItemKind, ConsoleItemStatus, ConsoleRow } from "../api";
import { C, FONT_MONO, TILE_HATCH, projectTile } from "../tokens";
import { columnWidth } from "../layout";
export { TABLE_COLUMN_WIDTH, READING_COLUMN_WIDTH } from "../layout";
import { ViewSkeleton, type ViewSkeletonShape } from "./Skeleton";
import { copyToClipboard } from "../lib/clipboard";
// The console and the phone must not have two ideas of how old a record is.
// Relative path, never the `ledai-backend/` alias — see
// `tests/guards/backendImportPath.test.ts`.
import { formatAge } from "../../../../backend/src/shared/age";
// The kind/severity/status vocabulary, shared with the phone's `RecordCard`.
import {
  RECORD_KIND_GLYPHS,
  RECORD_KIND_LABELS,
  RECORD_KIND_LABELS_PLURAL,
  RECORD_KIND_TONES,
  RECORD_SEVERITY_TONES,
  RECORD_STATUS_TONES,
} from "../../../../backend/src/shared/recordChrome";

// ---------------------------------------------------------------------------
// Type
// ---------------------------------------------------------------------------

/** The design's small-caps mono meta line, which runs through every screen
 *  (spec §6.2). Callers override `fontSize` and `color`; nothing else. */
export const META: React.CSSProperties = {
  fontFamily: FONT_MONO,
  fontSize: 10.93,
  fontWeight: 700,
  letterSpacing: "1.2px",
  textTransform: "uppercase",
};

/** How long a copy confirmation stays up. */
const COPY_CONFIRM_MS = 1400;

/**
 * The copy button that sits at the right of a field row.
 *
 * Lifted out of Item detail's own `Field` so the shared review-style field
 * table can carry it too. It confirms IN PLACE — a tick where the icon was —
 * because a copy that reports success somewhere else is a copy the reader has
 * to verify by pasting somewhere to find out.
 */
export function CopyValueButton({ label, value }: { label: string; value: string }) {
  const empty = value === "Not set" || value === "—" || value === "";
  const [state, setState] = useState<"idle" | "done" | "failed">("idle");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);

  const copy = async () => {
    if (empty) return;
    const ok = await copyToClipboard(value);
    setState(ok ? "done" : "failed");
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setState("idle"), COPY_CONFIRM_MS);
  };

  return (
    <button
      data-testid="copy-field"
      data-copy-state={state}
      onClick={() => void copy()}
      disabled={empty}
      title={
        state === "done"
          ? `Copied ${label}`
          : state === "failed"
            ? "Copy failed"
            : `Copy ${label}`
      }
      aria-label={`Copy ${label}`}
      className="kb"
      style={{
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 28,
        height: 28,
        marginTop: 1,
        borderRadius: 8,
        border: "none",
        background: "transparent",
        cursor: empty ? "default" : "pointer",
        // Invisible until the row is hovered — the affordance introduces
        // itself where it lives. `.frow` is the hover surface.
        opacity: empty ? 0 : undefined,
      }}
    >
      {state === "done" ? (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path
            d="M20 6 9 17l-5-5"
            stroke={C.green}
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <rect
            x="9"
            y="9"
            width="11"
            height="11"
            rx="2.5"
            stroke={state === "failed" ? C.red : C.sub2}
            strokeWidth="1.8"
          />
          <path
            d="M15 5.5H6.5A1.5 1.5 0 0 0 5 7v8.5"
            stroke={state === "failed" ? C.red : C.sub2}
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

/** The design writes month names in exactly this form; `ReviewQueue`'s
 *  date chip reads the same table rather than spelling a second one. */
export const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

/** The same days, abbreviated — the daily log's week strip, where seven of
 *  them share a row and the full names would not fit. Spelled out for the
 *  same reason `WEEKDAYS` is: a locale must not change what a screenshot
 *  says. Sunday-first, so `getDay()` indexes it directly. */
export const WEEKDAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * Month and weekday names are spelled out here rather than taken from
 * `toLocaleString`, so a screenshot, a test and a browser in another locale
 * all read the same. The design writes them in exactly this form.
 */
export function clockOf(date: Date): string {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const suffix = hours < 12 ? "a" : "p";
  const twelve = hours % 12 || 12;
  return `${twelve}:${String(minutes).padStart(2, "0")}${suffix}`;
}

/**
 * `"Aug 6, 2026"` — every date the console prints carries its year, this
 * year's included (LED-629). These are field records referenced months later
 * and in disputes, so the year is part of the record, and a date that shows
 * it on one screen and not the next is worse than a slightly longer string.
 * Use this, or the two stamps below that are built on it, for any date.
 */
export function dateWithYear(iso: string | Date): string {
  const date = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(date.getTime())) return "—";
  return `${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

/** `"Aug 6, 2026 · 7:14a"` — the design's timeline stamp. Rendered uppercase
 *  by the mono meta style, so it is not uppercased here. */
export function whenStamp(iso: string | Date): string {
  const date = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(date.getTime())) return "—";
  return `${dateWithYear(date)} · ${clockOf(date)}`;
}

/** `"Friday · Aug 7, 2026"` — the Brief's date line, the Daily Log's day
 *  header, and a walk's day. */
export function dayLabel(iso: string | Date): string {
  const date = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(date.getTime())) return "—";
  return `${WEEKDAYS[date.getDay()]} · ${dateWithYear(date)}`;
}

/** `"3 items"` / `"1 item"`. The design pluralizes in code everywhere and
 *  never writes "(s)"; this is that rule, spelled once. */
export function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

// ---------------------------------------------------------------------------
// Kind, severity, status
// ---------------------------------------------------------------------------

/**
 * The kind glyphs, labels and tones, and the severity and status chips, now
 * live in `backend/src/shared/recordChrome.ts` — the one place the console AND
 * the phone's `RecordCard` can both read. The phone cannot import from
 * `webapp/` (the dependency runs the other way), so the tables moved rather
 * than being copied: a second spelling of "what colour is a punch item" is a
 * second thing that can disagree with the first.
 *
 * Every export below keeps its old name, its old type and its old value, so
 * no console call site changed and nothing renders differently.
 */

/** Icon paths verbatim from the design's `KIND_ICON` table. */
export const KIND_ICON: Record<ConsoleItemKind, { d1: string; d2: string }> = RECORD_KIND_GLYPHS;

export const KIND_LABEL: Record<ConsoleItemKind, string> = RECORD_KIND_LABELS;

/**
 * The same labels PLURALIZED, for the blocks that count a SET of records
 * rather than name one of them: "Daily logs 4" reads as a count where
 * "Daily log 4" reads as a name. `MemberUsage`'s coverage block drew this
 * distinction first in a table of its own; this is that table, spelled once.
 */
export const KIND_LABEL_PLURAL: Record<ConsoleItemKind, string> = RECORD_KIND_LABELS_PLURAL;

/**
 * Kind colors for an INLINE citation chip.
 *
 * `KindChip` above is deliberately monochrome, because it labels a row that is
 * already visually a row. A chip embedded in running prose has no such frame:
 * without color it reads as emphasis rather than as a control. Every value
 * here is a token the console already uses elsewhere — no new palette.
 *
 * Stays `Record<string, …>`: `citePalette` below looks up an arbitrary wire
 * string and leans on the miss.
 */
export const KIND_CITE: Record<string, { ink: string; bg: string }> = RECORD_KIND_TONES;

/**
 * The palette for a citation `recordKind` this console cannot name — an
 * unrecognised kind from a newer backend, or one entirely missing (a legacy
 * `AskLedHistory` row written before `recordKind` existed).
 *
 * Deliberately NOT `walk`'s `{ ink: C.sub, bg: C.faint }` pairing: `CiteChip`
 * used to fall back to that exact palette, which made an unknown citation
 * visually identical to a walk citation — the confusion `SourcesCard`'s own
 * "Record" fallback chip exists to avoid. `citePalette` is the one place
 * every citation-drawing component resolves this, so the console cannot have
 * two opinions about what "unknown" looks like.
 */
export const KIND_CITE_UNKNOWN: { ink: string; bg: string } = { ink: C.ink, bg: C.line2 };

/** `KIND_CITE[recordKind]`, with the one fallback every citation consumer
 *  must agree on — see `KIND_CITE_UNKNOWN`. */
export function citePalette(recordKind: string): { ink: string; bg: string } {
  return KIND_CITE[recordKind] ?? KIND_CITE_UNKNOWN;
}

/**
 * Citation label text. `CiteChip` (an inline chip) and `SourcesCard` (a
 * source row) both name a `recordKind` in one short word, and until now each
 * had its own idea of what an unnamed one reads as: `SourcesCard` said
 * "Record", `askCitations.ts`'s `citationLabels` printed the raw wire string
 * (a future kind would render as e.g. lowercase `rfi` mid-sentence), and a
 * MISSING `recordKind` — a legacy history row from before this feature
 * shipped — produced no label at all, leaving `CiteChip` to render a bare
 * dot. One table, one fallback, colocated with `KIND_CITE` because the two
 * vocabularies must never drift apart.
 */
export const CITE_LABEL: Record<string, string> = {
  log: "Log",
  observation: "Obs",
  task: "Task",
  punch: "Punch",
  walk: "Walk",
};

/** What an unrecognised — or entirely missing — `recordKind` reads as,
 *  everywhere a citation is drawn. */
export const CITE_LABEL_UNKNOWN = "Record";

/** `CITE_LABEL[recordKind]`, with the one fallback every citation consumer
 *  must agree on — see `CITE_LABEL_UNKNOWN`. Bracket access on `undefined`
 *  (a legacy reference with no `recordKind` at all) safely misses the table
 *  and falls through to the same fallback as an unrecognised string. */
export function citeLabel(recordKind: string | undefined): string {
  return CITE_LABEL[recordKind as string] ?? CITE_LABEL_UNKNOWN;
}

export const SEVERITY: Record<string, { label: string; ink: string; bg: string }> =
  RECORD_SEVERITY_TONES;

/** The design's status chips: open amber, ready-for-review green, logged
 *  grey, plus the two the design's demo data never reaches. */
export const STATUS_CHIP: Record<ConsoleItemStatus, { label: string; ink: string; bg: string }> =
  RECORD_STATUS_TONES;

/** The accessors the phone's `RecordCard` reads the same tables through.
 *  Re-exported so the console has one import for the vocabulary, not two. */
export {
  kindGlyph,
  kindLabel,
  kindTone,
  severityTone,
  statusTone,
} from "../../../../backend/src/shared/recordChrome";

/**
 * The kind's icon, bare — no chip drawn around it.
 *
 * A row that leads with a `KindChip` and then prints the kind AGAIN as its
 * own text reads "DAILY LOG  Daily log": the same fact, twice, an inch apart.
 * The phone says it once — one glyph, one name, the count on the right — and
 * this is that glyph.
 *
 * Use it wherever the text beside it already names the kind. Use `KindChip`
 * where the text beside it is a TITLE, which is the case the chip exists for.
 */
export function KindGlyph({ kind, size = 15 }: { kind: ConsoleItemKind; size?: number }) {
  const icon = KIND_ICON[kind];
  return (
    <svg
      data-testid="kind-glyph"
      data-kind={kind}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={C.sub}
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      // Same reason as the chip's `flexShrink: 0` — the label beside it gives
      // way, never the mark that says which kind the row is.
      style={{ flexShrink: 0 }}
      aria-hidden="true"
    >
      <path d={icon.d1} />
      <path d={icon.d2} />
    </svg>
  );
}

export function KindChip({ kind, label }: { kind: ConsoleItemKind; label?: string }) {
  const icon = KIND_ICON[kind];
  return (
    <span
      data-testid="kind-chip"
      style={{
        ...META,
        display: "inline-flex",
        alignItems: "center",
        // The chip is a label, never the thing that gives way: the TITLE
        // beside it truncates instead (see `ROW_TITLE`).
        flexShrink: 0,
        gap: 5,
        fontSize: 9.77,
        letterSpacing: "0.7px",
        color: C.sub,
        background: C.faint,
        padding: "3px 7px",
        borderRadius: 5,
      }}
    >
      <svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="none"
        stroke={C.sub}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d={icon.d1} />
        <path d={icon.d2} />
      </svg>
      {label ?? KIND_LABEL[kind]}
    </span>
  );
}

/**
 * The console's filter chip — a pill that carries a label, its count, and
 * whether it is currently narrowing the list.
 *
 * ## Why it is here and not in a view
 *
 * It was written twice. The Daily log had one (design 11a) and Walks had
 * another inline in its header, and the two had drifted apart in every
 * number that could drift: 32px tall against 30, 12px of padding against 13,
 * a surface ground against a transparent one, `hair` against `line2`, 13px
 * type against 12.5. Nobody chose any of those differences — they are what
 * happens when one control is built from memory in two places.
 *
 * The Daily log's is the one that survived, because it is the newer of the
 * two and the only one that had grown a `dot` for the categories that need
 * attention. Walks now draws the same control rather than its own copy.
 *
 * `count` is optional: a chip filtering to something uncounted is still a
 * chip, and a zero is a fact worth printing where an absent number is not.
 */
export function FilterChip({
  label,
  count,
  active,
  dot,
  onClick,
  testId = "filter-chip",
}: {
  label: string;
  count?: number;
  active: boolean;
  /** A red pip, for a category whose presence is itself the warning. */
  dot?: boolean;
  onClick: () => void;
  testId?: string;
}) {
  return (
    <button
      type="button"
      className="kb chipswap"
      data-testid={testId}
      data-active={active ? "true" : "false"}
      aria-pressed={active}
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        height: 32,
        padding: "0 12px",
        borderRadius: 999,
        background: active ? C.ink : C.surface,
        color: active ? C.surface : C.ink,
        border: active ? "1px solid transparent" : `1px solid ${C.hair}`,
        font: "inherit",
        fontSize: 14.95,
        fontWeight: active ? 600 : 500,
        cursor: "pointer",
      }}
    >
      {dot && (
        <span
          aria-hidden
          style={{ width: 6, height: 6, borderRadius: 3, background: C.red, flexShrink: 0 }}
        />
      )}
      {label}
      {count !== undefined && (
        <span
          style={{
            fontFamily: FONT_MONO,
            fontSize: 11.5,
            color: active ? C.surface : C.sub2,
            opacity: active ? 0.6 : 1,
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

export function Pill({ label, ink, bg }: { label: string; ink: string; bg: string }) {
  return (
    <span
      style={{
        ...META,
        fontSize: 9.77,
        letterSpacing: "0.6px",
        // Same reason as `KindChip` — a severity that wrapped to its own line
        // would be the bug this style exists to prevent, one pill over.
        flexShrink: 0,
        color: ink,
        background: bg,
        padding: "2px 7px",
        borderRadius: 5,
      }}
    >
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

/**
 * The line a work row leads with: the kind pill, the title, and any severity
 * pill after it. Spelled ONCE because eight views drew it by hand and all
 * eight had the same bug.
 *
 * It does not wrap. It used to (`flexWrap: "wrap"`), and a long title —
 * "Third Floor Walkthrough – Clear Weather, 70°F, …" — moved to a second
 * line, leaving the kind pill alone on the first. Every reader described that
 * the same way: *the daily-log pill is rendering above the item*. On the
 * Brief's Moved rows, which reserve a FIXED height, the second line also
 * overflowed the row it was given.
 *
 * The TITLE gives way instead — see `ROW_TITLE`. Nothing else on the line
 * does: `KindChip` and `Pill` both set `flexShrink: 0`, because a chip that
 * shrank would only move the same problem sideways.
 */
export const ROW_LINE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  minWidth: 0,
};

/**
 * The title on that line, truncated with an ellipsis rather than wrapped.
 *
 * `minWidth: 0` is the load-bearing half and the one that is easy to drop: a
 * flex item's automatic minimum size is its CONTENT width, so `overflow` and
 * `text-overflow` on their own do nothing — the item simply refuses to get
 * smaller and pushes the line wide. Callers spread this and then add their
 * own type; they must not re-add `whiteSpace` or `overflow`.
 */
export const ROW_TITLE: React.CSSProperties = {
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

/**
 * The age column.
 *
 * The TEXT rule no longer lives here. It used to be `today` for anything under
 * a day and `Nd` otherwise, which is a granularity a console can afford and a
 * review queue cannot — the phone's queue is read in minutes. Both surfaces now
 * share `formatAge` (`backend/src/shared/age.ts`): `now`, `10m`, `9h`, `2d`,
 * always rounded DOWN so a row never claims to be older than it is. A sub-day
 * row therefore reads `3h` here where it used to read `today`, and a 2.6-day
 * row reads `2d` where the old `Math.round` said `3d`.
 *
 * Its INK is `stalled` or not, and nothing else. The design's own
 * `days>=6 ? red : days>=3 ? amber : sub` ladder is built from demo
 * constants; the real stall line is org policy, resolved server-side at query
 * time, and `ConsoleRow.stalled` already carries that verdict. A second,
 * hardcoded ladder on the client would be a second place that can disagree
 * with policy about what counts as stuck.
 *
 * ## Why the INSTANT and not `daysSinceMovement`
 *
 * This used to reconstruct the instant as `Date.now() - daysSinceMovement *
 * DAY_MS`, and that quietly broke the round-DOWN promise above.
 * `daysSinceMovement` is not the raw age: `consoleItem.ts`'s `roundDays`
 * quantizes it to a tenth of a day with `Math.round`, half of which rounds
 * UP. The reconstructed instant was therefore up to 1.2h OLDER than the real
 * one, and flooring an already-inflated number cannot un-inflate it — a 3.00h
 * row printed `2h`, a 1.70h row printed `2h`, a 23.04h row printed `1d`, a
 * 5.97d row printed `6d`, and anything under 7.2m printed `now`.
 *
 * `lastMovementAt` is the instant the projection derived that number FROM,
 * and the row already carries it, so the fix is to stop deriving. Nothing
 * else changes: `daysSinceMovement` keeps serving sorting, the age column
 * filter and the stall comparison exactly as before. `ageOf.test.ts` pins the
 * boundaries.
 */
export function ageOf(
  row: Pick<ConsoleRow, "lastMovementAt" | "daysSinceMovement" | "stalled">,
): {
  text: string;
  ink: string;
} {
  const movedAt = Date.parse(row.lastMovementAt ?? "");
  return {
    // The projection refuses to project a record with no `lastMovementAt`, so
    // the fallback is for hand-built rows only. It is the old, imprecise
    // reconstruction on purpose: `formatAge(NaN)` reads `now`, which is a
    // fresh-looking lie about a record that may be weeks old.
    text: formatAge(
      Number.isFinite(movedAt) ? movedAt : Date.now() - row.daysSinceMovement * DAY_MS,
    ),
    ink: row.stalled ? C.red : C.sub,
  };
}

/** The `daysSinceMovement` fallback above is a count of days; `formatAge`
 *  takes an instant. */
const DAY_MS = 86_400_000;

/** A person's initials for an avatar chip: `Sam Chu` -> `SC`,
 *  `sam.chu@example.com` -> `SC`, one word -> its first two letters, nothing
 *  -> `?`. Generic on purpose; it has no opinion about what the name means. */
export function initialsOf(name: string | undefined): string {
  if (!name) return "?";
  const words = name.replace(/[._@-]+/g, " ").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

/** `open` -> `Open`, `ready_for_review` -> `Ready for review`. The server's
 *  status vocabulary is snake_case by design (`consoleItem.ts`); the display
 *  form is a presentation concern and lives here, not in the projection. */
export function humanize(label: string): string {
  const spaced = label.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}


/**
 * The design's evidence tile: a 45°-hatched gradient in the project's own
 * tone, with the presigned photo laid over it when there is one.
 *
 * The gradient is not a placeholder for a failed load — it is the tile, and
 * the photo sits on top. A `null` url (R2 unconfigured, or a stored reference
 * this deployment cannot resolve) therefore degrades to a tile that still
 * reads as evidence rather than to a broken-image icon.
 *
 * There is no video player and no audio anywhere near this component. A
 * `video` evidence row renders its poster tile and its caption; playback is
 * not part of v1, and audio never reaches the client at all (spec §7.3).
 */
export function EvidenceTile({
  jobSiteId,
  url,
  height,
  maxHeight,
  fit = "cover",
  stamp,
  alt,
}: {
  jobSiteId: string;
  url?: string | null;
  height: number;
  /** `fit="natural"` only: how tall the tile may grow once it knows the
   *  photo's shape. A portrait at full column width is otherwise most of a
   *  screen. */
  maxHeight?: number;
  /**
   * How the photo sits in the tile.
   *
   * `cover` — the tile's box is the shape, and the photo is cropped to fill
   * it. Right for a GRID or a row thumbnail, where a ragged set of shapes
   * would be noise and no single photo is the subject.
   *
   * `natural` — the photo's own shape decides the box. Right where the photo
   * IS the content: the item record shows two or three of them at full column
   * width, and cropping a portrait to a 240px band there showed a horizontal
   * slice of somebody's evidence (user, 2026-09-11: photos on the observation
   * detail "don't always show up in the right aspect ratio").
   *
   * Nothing stores the dimensions — neither `ConstructionNoteMediaReference`
   * nor the console's own evidence row carries width or height — so the ratio
   * is learned from the image when it loads. Until then the tile keeps
   * `height` and the photo is contained rather than cropped, so what is on
   * screen is never a lie about the photo, only smaller than it will be.
   */
  fit?: "cover" | "natural";
  stamp?: string;
  alt?: string;
}) {
  const [ratio, setRatio] = useState<number | null>(null);
  const natural = fit === "natural";
  return (
    <span
      style={{
        display: "block",
        position: "relative",
        ...(natural && ratio !== null
          ? { aspectRatio: String(ratio), ...(maxHeight ? { maxHeight } : {}) }
          : { height }),
        borderRadius: 11,
        overflow: "hidden",
        background: projectTile(jobSiteId),
        border: `1px solid ${C.line}`,
      }}
    >
      <span style={{ position: "absolute", inset: 0, opacity: 0.22, backgroundImage: TILE_HATCH }} />
      {url && (
        <PhotoImage
          src={url}
          alt={alt ?? ""}
          // A list of 170 records is a list of 170 photos. `lazy` means the
          // browser fetches the ones near the viewport and no others, which
          // is the difference between a page that settles and a page that
          // spends a minute pulling images nobody scrolled to; `async`
          // decoding keeps the ones it does fetch off the main thread.
          loading="lazy"
          decoding="async"
          onLoad={
            natural
              ? (event) => {
                  const image = event.currentTarget;
                  // A decode that reports 0 tells us nothing; leaving `ratio`
                  // null keeps the fixed box, which is the honest fallback.
                  if (image.naturalWidth > 0 && image.naturalHeight > 0) {
                    setRatio(image.naturalWidth / image.naturalHeight);
                  }
                }
              : undefined
          }
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            // `contain` even once the box matches the photo: with the right
            // ratio the two are identical, and it is what keeps the clamped
            // case (a very tall portrait against `maxHeight`) whole instead of
            // cropped.
            objectFit: natural ? "contain" : "cover",
          }}
        />
      )}
      {stamp && (
        <span
          style={{
            ...META,
            position: "absolute",
            left: 8,
            bottom: 8,
            fontSize: 9.77,
            letterSpacing: "0.7px",
            color: C.surface,
            background: C.ink,
            padding: "3px 6px",
            borderRadius: 5,
          }}
        >
          {stamp}
        </span>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Containers
// ---------------------------------------------------------------------------

/** The design's card shell, verbatim: surface, hairline, 16px radius,
 *  19/21 padding, one-pixel shadow. */
export function Card({
  children,
  style,
  testId,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
  testId?: string;
}) {
  return (
    <div
      data-testid={testId}
      style={{
        background: C.surface,
        // The redesign's card: a 7% hairline and TWO soft shadow layers — the
        // near one is the edge, the far one the lift. A single hard shadow
        // over a 10% border drew a box; this reads as a surface raised off
        // the page ground.
        border: `1px solid ${C.hair}`,
        borderRadius: 14,
        padding: "19px 21px",
        boxShadow: `0 1px 2px ${C.softShadow}, 0 4px 12px ${C.softShadowLow}`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** The design's section eyebrow: mono small caps on the left, an optional
 *  mono rule or link on the right. */
export function Eyebrow({
  children,
  right,
  style,
}: {
  children: React.ReactNode;
  right?: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        marginBottom: 8,
        ...style,
      }}
    >
      <span style={{ ...META, fontSize: 10.93, letterSpacing: "1.3px", color: C.ink }}>
        {children}
      </span>
      {right}
    </div>
  );
}

export function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "80px 20px",
        textAlign: "center",
        flex: 1,
      }}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The three states every view has to be able to be in
// ---------------------------------------------------------------------------

/** What each status the routes distinguish MEANS to the person reading the
 *  page. Spelled once so no view invents its own wording for a 403. */
export function failureHeadline(status: number): string {
  switch (status) {
    case 401:
      return "You are not signed in";
    case 403:
      return "That workspace is not yours";
    case 400:
      return "The console asked for something that does not exist";
    case 404:
      return "That item is not in this workspace";
    case 503:
      return "The console cannot reach the database";
    default:
      return "The console could not load this view";
  }
}

/**
 * A failure, rendered AS a failure.
 *
 * `data-testid="error-state"`, an error-red eyebrow and the server's own
 * message — deliberately nothing like `EmptyPanel`. A 403 or a 400 also
 * offers the way OUT of a workspace the caller may not have: without it a
 * forged or stale `?workspace=` in a bookmark is a dead end, since the
 * failing response carries no workspace list to switch from.
 */
export function FailurePanel({
  failure,
  personalHref,
  onGoPersonal,
}: {
  failure: ApiError;
  personalHref?: string;
  onGoPersonal?: () => void;
}) {
  const recoverable = failure.status === 403 || failure.status === 400;
  return (
    <Centered>
      <div data-testid="error-state" style={{ ...META, fontSize: 10.93, color: C.red }}>
        {failure.status ? `Error ${failure.status}` : "Network error"}
      </div>
      <div style={{ fontSize: 20.7, fontWeight: 700, letterSpacing: "-0.4px", marginTop: 12 }}>
        {failureHeadline(failure.status)}
      </div>
      <div
        style={{
          fontSize: 15.52,
          color: C.sub,
          marginTop: 6,
          maxWidth: 460,
          lineHeight: 1.5,
          fontFamily: FONT_MONO,
        }}
      >
        {failure.message}
      </div>
      {recoverable && personalHref && onGoPersonal && (
        <a
          href={personalHref}
          data-testid="recover-personal"
          onClick={(event) => {
            event.preventDefault();
            onGoPersonal();
          }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            marginTop: 18,
            height: 36,
            padding: "0 14px",
            borderRadius: 10,
            background: C.faint,
            fontSize: 14.37,
            fontWeight: 700,
          }}
        >
          Go to your Personal workspace
        </a>
      )}
    </Centered>
  );
}

/** The design's glasses mark, which it draws in every empty state. */
function GlassesMark() {
  return (
    <span
      style={{
        width: 52,
        height: 52,
        borderRadius: 15,
        background: C.sunk,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <svg width="24" height="15" viewBox="0 0 28 16" fill="none">
        <circle cx="6" cy="9" r="5" stroke={C.sub2} strokeWidth="1.8" />
        <circle cx="22" cy="9" r="5" stroke={C.sub2} strokeWidth="1.8" />
        <path d="M11 9h6" stroke={C.sub2} strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    </span>
  );
}

/**
 * Nothing to show, and that is the ANSWER.
 *
 * `data-testid="empty-state"`, no error ink, no status code — and a body that
 * says what would have to happen for something to appear here. An empty view
 * and a broken one must never be the same pixels.
 */
export function EmptyPanel({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  /**
   * A way OUT of the empty state, when the view has one to offer.
   *
   * Optional because most empty states genuinely have no next step — an empty
   * workspace has nothing to route to. But an empty DAY does: some other day
   * has entries, and a page that says "nothing logged today" and stops is a
   * dead end the reader has to escape by editing the URL. Where a view can
   * name the next place to look, it should.
   */
  action?: { label: string; href: string; onClick: () => void };
}) {
  return (
    <Centered>
      <GlassesMark />
      <div
        data-testid="empty-state"
        style={{ fontSize: 20.7, fontWeight: 700, letterSpacing: "-0.4px", marginTop: 16 }}
      >
        {title}
      </div>
      <div style={{ fontSize: 15.52, color: C.sub, marginTop: 6, maxWidth: 400, lineHeight: 1.5 }}>
        {body}
      </div>
      {action && (
        <a
          className="kb"
          data-testid="empty-state-action"
          href={action.href}
          onClick={(event) => {
            event.preventDefault();
            action.onClick();
          }}
          style={{
            marginTop: 16,
            fontSize: 14.95,
            fontWeight: 700,
            color: C.ink,
            borderBottom: `2px solid ${C.gold}`,
            paddingBottom: 1,
          }}
        >
          {action.label}
        </a>
      )}
    </Centered>
  );
}

/**
 * The first load, before there is anything to say.
 *
 * A CONTENT-SHAPED skeleton, not a centred word (spec §1.3). The word
 * "Loading" in the middle of an empty frame told the reader nothing about
 * what was coming and, worse, occupied a different amount of space than the
 * view did — so every view switch ended in the page jumping once the data
 * landed. The skeleton reserves the shape instead.
 *
 * `shape` is optional and defaults to `rows`, which is what every list-shaped
 * view in this console is. A view that is a different shape says so; a view
 * that says nothing still gets a skeleton rather than a bare line.
 */
export function LoadingPanel({ shape }: { shape?: ViewSkeletonShape } = {}) {
  return <ViewSkeleton {...(shape ? { shape } : {})} />;
}

/**
 * A refresh failed while the page already had good data.
 *
 * The rows STAY — see `useResource`. What changes is that the page stops
 * claiming to be current: a list that quietly froze while still looking live
 * is worse than one that admits it is stale, because only the console knows
 * whether the user's next move should be "reload" or "trust it".
 */
export function StaleBanner({ failure, since }: { failure: ApiError; since?: string }) {
  return (
    <div
      data-testid="stale-banner"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        padding: "8px 13px",
        borderRadius: 10,
        background: C.amberSoft,
        border: `1px solid ${C.line}`,
        marginBottom: 14,
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: 3, background: C.amber, flexShrink: 0 }} />
      <span style={{ fontSize: 14.37, color: C.sub }}>
        {`Could not refresh (${failure.status || "network error"}). Showing what loaded${
          since ? ` at ${clockOf(new Date(since))}` : ""
        }.`}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The view header every screen shares
// ---------------------------------------------------------------------------

/** The design's page header: mono crumb · mono count, then the 25px title,
 *  with a mono note on the right. */
export function ViewHeader({
  crumb,
  count,
  title,
  note,
  actions,
  children,
  titleStyle,
  wide,
}: {
  crumb?: string;
  count?: string;
  title: string;
  note?: string;
  /**
   * Controls that belong on the TITLE's baseline rather than under it.
   *
   * `children` renders below the title, which is right for a filter row and
   * wrong for a page's primary action, which belongs beside the title it
   * acts on. Dropping one a row put a full-width band of controls between
   * the page's name and its content. (The daily log's header carried its
   * stepper and an accept button here; both are gone, and the slot stays for
   * the next page that needs it.)
   */
  actions?: React.ReactNode;
  children?: React.ReactNode;
  /** An individual detail view may need a slightly more prominent title
   * without changing the console-wide header scale. */
  titleStyle?: React.CSSProperties;
  /** A reading-heavy detail page (the walk) opts into a slightly roomier
   *  980px column — header and body together, never one. */
  wide?: boolean | "table" | "record";
}) {
  return (
    <div style={{ flexShrink: 0, padding: "22px 0 0" }}>
      <div
        className="pagecol"
        data-testid="view-header-column"
        style={{
          width: "100%",
          maxWidth: columnWidth(wide),
          margin: "0 auto",
        }}
      >
        <div
          style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
          }}
        >
          <div>
            {(crumb || count !== undefined) && <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
              {crumb && <span style={{ ...META, color: C.sub }}>{crumb}</span>}
              {count !== undefined && (
                <>
                  {crumb && <span style={{ width: 3, height: 3, borderRadius: 2, background: C.line2 }} />}
                  <span
                    style={{ ...META, fontWeight: 400, color: C.sub2 }}
                    data-testid="view-count"
                  >
                    {count}
                  </span>
                </>
              )}
            </div>}
            <div
              style={{
                // The redesign sets a page title at 24/700, not 25/800. The
                // heavier weight was competing with the content under it on
                // every screen; a page title only has to name the page.
                fontSize: 24,
                fontWeight: 700,
                letterSpacing: "-0.5px",
                marginTop: crumb || count !== undefined ? 4 : 0,
                ...titleStyle,
              }}
            >
              {title}
            </div>
          </div>
          {(actions || note) && (
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {note && <div style={{ ...META, fontSize: 10.35, color: C.sub2 }}>{note}</div>}
              {actions}
            </div>
          )}
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * The room the Ask Led bubble needs, reserved by the SHELL's main pane.
 *
 * The bubble is `position: fixed` bottom-right over every screen, so the room
 * has to come out of the VIEWPORT, not out of a scrolling body: padding at
 * the end of a scroll container only protects the last block, and any control
 * in the middle of a long page can still be scrolled into the corner the
 * bubble occupies. The browser suite found exactly that — a policy toggle
 * scrolled under the bubble, and the click meant for it opened Ask Led
 * instead (`reviewViews.console.spec.ts`, "issues ZERO writes").
 *
 * So `Shell.tsx` insets the whole main pane by this much whenever the bubble
 * is mounted, which makes "no page control is ever underneath it" true by
 * geometry rather than by each view remembering a spacer. It used to be a
 * spacer the Brief added by hand, which worked only because the Brief was the
 * one screen with a bubble.
 *
 * Declared here rather than in `AskBubble.tsx` so `chrome.tsx` does not have
 * to import the component that imports `chrome.tsx`; re-exported there, where
 * a reader looks for it.
 */
export const ASK_BUBBLE_GUTTER = 62;

/** The sidebar's width. The settled pane design (“Right pane edge options”,
 *  option B — flush) draws the Ask composer `left: <sidebar>`, so the shell
 *  and the composer must agree on the number; this is the one place it is
 *  written. 256 comes from the console redesign (3a), which widened the rail
 *  from the 232 of its own first pass — and narrowed it from this console's
 *  268 — so the nav rows read as surfaces with room around them. The shell +
 *  Home redesign (LED-1151, `home-projects.dc.html`) settles it at 252, with
 *  a 68px icon rail. */
export const SIDEBAR_WIDTH = 252;

/**
 * The room the fixed Ask composer needs at the END of every scrolling body
 * (the settled design's `.body` bottom padding). Unlike the old bubble —
 * which floated over one corner and needed the whole pane inset — the
 * composer spans the bottom over a fade, and content is EXPECTED to scroll
 * under the fade; this clearance is what guarantees every control can be
 * scrolled fully above it.
 *
 * The arithmetic, so the next person can redo it rather than guess: the
 * suggestion strip is ~41px (a pill plus the 8px gap), the card ~90px (its
 * padding, the input, the button row) and the rail's own bottom padding is
 * 16px — about 147px of composer. That is exactly what this number used to
 * be, and a clearance equal to the thing it clears leaves the last row of a
 * page resting ON the composer's edge: technically above it, still unreadable
 * under the fade's top stop. The extra ~60px is that reading room — scroll a
 * page to its end and the last row now sits clear of the composer with air
 * under it (user-directed, 2026-09-04). The strip keeps its space in the
 * layout when it withdraws, so this number does not change with it.
 */
export const ASK_COMPOSER_CLEARANCE = 208;

/** The measure shared by the console's dense data tables and list pages. */

/** The measure for a page that is READ rather than scanned — the walk detail,
 *  and the review card's record column. Narrower than a table on purpose: a
 *  field row whose label and value sit at opposite ends of 1100px is a row
 *  nobody reads as one row. */

/** The four content-column widths the console has: the default, the reading
 *  column, the standard data-table width, and the item record's. */

// ---------------------------------------------------------------------------
// Long lists — how much of one is on screen
// ---------------------------------------------------------------------------

/** Rows added each time the reader reaches the end of what is drawn. */
export const PAGE_STEP = 40;

/**
 * How much of a long list is rendered, growing as the reader reaches the end.
 *
 * The console reads the whole workspace in one request and always has — that
 * is what makes its counts trustworthy and its filters instant. What it must
 * not do is DRAW all of it: 170 rows is 170 evidence tiles, and a browser
 * asked for 170 images at once spends the first seconds of every visit
 * fetching pictures of records nobody has scrolled to.
 *
 * So the data stays whole and the RENDER is paged. A sentinel 600px below the
 * last drawn row asks for the next `PAGE_STEP` before the reader reaches the
 * bottom, which is why there is no spinner: the rows are simply there.
 *
 * `resetKey` is the list's IDENTITY — the view, the filters, the project.
 * Changing it starts again at the top; a live refresh of the same list must
 * NOT, or the console would yank a reader back to row 40 every time the data
 * came round again.
 *
 * Where `IntersectionObserver` does not exist — a test DOM, an old engine —
 * the whole list renders. A list that silently stopped at row 40 with no way
 * to ask for more is the one outcome worse than a slow page.
 */
export function usePagedList(resetKey: string, total: number, step = PAGE_STEP) {
  const supported = typeof IntersectionObserver !== "undefined";
  const [shown, setShown] = useState(supported ? step : Number.MAX_SAFE_INTEGER);
  const sentinel = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setShown(supported ? step : Number.MAX_SAFE_INTEGER);
  }, [resetKey, step, supported]);

  useEffect(() => {
    const node = sentinel.current;
    if (!node || !supported || shown >= total) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShown((count) => Math.min(total, count + step));
        }
      },
      { rootMargin: "600px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [shown, total, step, supported]);

  return { shown: Math.min(shown, total), sentinel, more: shown < total };
}

/**
 * The end of a paged list: the marker the observer watches, and a quiet line
 * saying how much of the list is drawn.
 *
 * The line is not a control. It is there because a list that stops at row 40
 * of 171 with nothing said about it reads as a list of 40.
 */
export function PagedListEnd({
  shown,
  total,
  noun,
  nouns,
  sentinel,
}: {
  shown: number;
  total: number;
  noun: string;
  nouns: string;
  sentinel: React.RefObject<HTMLDivElement | null>;
}) {
  if (shown >= total) return null;
  return (
    <div
      ref={sentinel}
      data-testid="paged-list-end"
      data-shown={shown}
      data-total={total}
      style={{
        ...META,
        fontSize: 10.35,
        color: C.sub2,
        padding: "18px 2px 6px",
        textAlign: "center",
      }}
    >
      {`${shown} of ${plural(total, noun, nouns)}`}
    </div>
  );
}

/**
 * The scrolling body under a `ViewHeader`.
 *
 * `header` takes the view's `ViewHeader` INSIDE the scroll region, so the
 * title, its crumb and whatever stat band or filter row it carries travel
 * with the list instead of standing still above it. Passed as a prop rather
 * than rendered as the first child because the header draws its own
 * `.pagecol` gutters: nested inside the body's content column it would be
 * indented by them twice.
 *
 * A view that leaves it out keeps the older arrangement — header pinned, body
 * scrolling under it — which is still right where the header is a toolbar the
 * reader acts on while scrolling.
 */
export function ViewBody({
  children,
  header,
  wide,
}: {
  children: React.ReactNode;
  header?: React.ReactNode;
  wide?: boolean | "table" | "record";
}) {
  return (
    <div
      className="sb"
      style={{
        flex: 1,
        minHeight: 0,
        overflow: "auto",
        // The bottom padding is the composer's clearance (the settled pane
        // design), so the last control on every page can always be scrolled
        // above the fixed composer. The top is 0 when the header rides along:
        // the header carries its own.
        padding: `${header ? 0 : 18}px 0 ${ASK_COMPOSER_CLEARANCE}px`, paddingBottom: `var(--ask-composer-clearance, ${ASK_COMPOSER_CLEARANCE}px)`,
      }}
    >
      {header}
      <div
        className="pagecol"
        data-testid="view-body-column"
        style={{ width: "100%", maxWidth: columnWidth(wide), margin: "0 auto", minWidth: 0 }}
      >
        {children}
      </div>
    </div>
  );
}

/** The row meta line the design writes under every title: site, then
 *  location, then category, in mono small caps, with the absent parts simply
 *  not there rather than rendered as gaps. */
export function metaLine(row: ConsoleRow, siteName: string | undefined): string {
  return [siteName || row.jobSiteId, row.location?.name, row.category]
    .filter(Boolean)
    .join(" · ");
}

/**
 * The design's quiet strip: a green check and a sentence, on the sunk tone,
 * used wherever ABSENCE is the answer and the answer is good news.
 *
 * It is not `EmptyPanel`. An empty panel says "there is nothing to show
 * here"; this says "we looked, and there is nothing to chase" — the Daily
 * Log's quiet-categories line and a Brief with nothing past the stall line
 * are both that second thing.
 */
export function QuietLine({ children }: { children: React.ReactNode }) {
  return (
    <div
      data-testid="quiet-line"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "10px 13px",
        borderRadius: 11,
        background: C.faint,
      }}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke={C.green}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ flexShrink: 0, marginTop: 1 }}
      >
        <path d="m5 13 4 4L19 7" />
      </svg>
      <span style={{ fontSize: 14.37, color: C.sub, lineHeight: 1.45 }}>{children}</span>
    </div>
  );
}

/**
 * What every full-page view is handed by the shell.
 *
 * `search` is the whole console URL query, forwarded verbatim to the API so a
 * view can never answer for a different workspace than the page claims; every
 * link a view builds goes back through the shell's `hrefWith`, for the same
 * reason. `refreshKey` changes when the live stream says something moved —
 * a REFRESH, not a navigation, which is what keeps a failed background poll
 * from blanking a good page (`useResource`).
 */
/**
 * A console URL with SEVERAL parameters rewritten and the rest preserved.
 *
 * `ConsoleViewProps.hrefWithParam` does ONE at a time, which is enough for
 * every in-page tab but not for a filter whose parameters must move together
 * — a date range writes `from` and `to` in one navigation because the route
 * rejects half of one.
 *
 * The rule `hrefWith` exists to enforce is what matters and is kept: the href
 * starts from the CURRENT `search`, so `?workspace=` and everything else
 * rides along. A link that spelled its own query from scratch would silently
 * answer for a different tenant than the page claims.
 *
 * `Brief.tsx` has a private twin of this that predates it. Prefer this one.
 */
export function hrefWithAll(
  search: string,
  entries: Array<[string, string | null]>,
): string {
  // Never parse a pathname as a query — see `Shell.tsx`'s `queryOf`.
  const at = search.indexOf("?");
  const rawQuery = at >= 0 ? search.slice(at + 1) : search.startsWith("/") ? "" : search;
  const params = new URLSearchParams(rawQuery);
  for (const [key, value] of entries) {
    if (value === null) params.delete(key);
    else params.set(key, value);
  }
  const query = params.toString();
  return query ? `?${query}` : location.pathname;
}

/** `search` with the view's `scopeSiteId` applied, for its data reads only. */
export function scopedSearch(search: string, scopeSiteId: string | undefined): string {
  if (!scopeSiteId) return search;
  const params = new URLSearchParams(search);
  params.set("site", scopeSiteId);
  return `?${params}`;
}

export interface ConsoleViewProps {
  search: string;
  /**
   * A project the page shows WITHOUT being scoped to it (LED-928): under All
   * projects, a source with exactly one project opens that project's page
   * in place rather than a list of one. Only the view's DATA reads narrow to
   * it (`scopedSearch`); every link it builds keeps `search`, so the URL —
   * and the pill — stay on All projects.
   */
  scopeSiteId?: string;
  refreshKey: number;
  /** The shell's review count, shared with Home across independent refreshes.
   *  Null means the current scope has not loaded yet. */
  pendingReviewCount?: number | null;
  /** The shell's own inventory (`/api/items` for this scope), so Home's
   *  widgets read the SAME response the nav counts come from rather than a
   *  second request. `undefined` outside the shell (a view rendered alone). */
  inventory?: import("../api").ItemsResponse | null;
  /** `?workspace=personal` on top of the current URL — the way out of a
   *  workspace the caller may not have. */
  personalHref: string;
  onGoPersonal: () => void;
  hrefForItem: (itemId: string) => string;
  onOpenItem: (itemId: string) => void;
  /**
   * `hrefWith` bound to the current console URL — ONE parameter rewritten,
   * every other preserved (pass `null` to remove one).
   *
   * A view uses this for its own in-page state (the Review queue's kind and
   * project tabs) rather than composing a query string itself, for the reason
   * the shell's header gives: a link that spelled its own query would
   * silently drop `?workspace=` and answer for a different tenant than the
   * page claims.
   */
  hrefWithParam: (key: string, value: string | null) => string;
  onNavigate: (href: string) => void;
  /** Project home. */
  hrefForProject: (jobSiteId: string) => string;
  onOpenProject: (jobSiteId: string) => void;
  /** Walk detail. */
  hrefForWalk: (visitId: string) => string;
  onOpenWalk: (visitId: string) => void;
  /** The Review queue, optionally filtered to one project. */
  hrefForReview: (jobSiteId?: string) => string;
  onOpenReview: (jobSiteId?: string) => void;
}
